import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Edit, Trash2, X, Check, Download, Loader2, ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { sportsService, Sport, WizardSport, WizardCompetition, getErrorMessage, xaccelService, XaccelServer } from '@/services';

const sportSchema = z.object({
  name: z.string().min(1, 'validation.required'),
  dazn_asset_id: z.string().optional(),
});

type SportForm = z.infer<typeof sportSchema>;

// ============================================================================
// Sortable Sport Card
// ============================================================================

interface SortableSportCardProps {
  sport: Sport;
  deleteConfirm: number | null;
  onEdit: (sport: Sport) => void;
  onDeleteConfirm: (id: number) => void;
  onDeleteCancel: () => void;
  onDelete: (id: number) => void;
}

function SortableSportCard({
  sport, deleteConfirm, onEdit, onDeleteConfirm, onDeleteCancel, onDelete,
}: SortableSportCardProps) {
  const { t } = useTranslation();
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: sport.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-dark-700 text-dark-500 hover:text-dark-300"
            {...attributes}
            {...listeners}
          >
            <GripVertical size={18} />
          </button>
          <div>
            <h3 className="font-medium text-dark-100">{sport.name}</h3>
            <p className="text-dark-400 text-sm">
              {sport.competitions_count ?? 0} {t('sports.competitions')}
            </p>
            {sport.dazn_asset_id && (
              <p className="text-dark-500 text-xs font-mono truncate max-w-[180px]">
                {sport.dazn_asset_id}
              </p>
            )}
          </div>
        </div>
        {deleteConfirm === sport.id ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDelete(sport.id)}
              className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500"
            >
              <Check size={16} />
            </button>
            <button
              onClick={onDeleteCancel}
              className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(sport)}
              className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-100"
            >
              <Edit size={16} />
            </button>
            <button
              onClick={() => onDeleteConfirm(sport.id)}
              className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function Sports() {
  const { t } = useTranslation();
  const [sports, setSports] = useState<Sport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSport, setEditingSport] = useState<Sport | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Wizard state
  const [showWizard, setShowWizard] = useState(false);
  // Servers the imported competitions start with: ticked by the user, the default one only pre-ticked as a suggestion.
  // Nothing is created on them here (channels are created from the competition's Canali tab, after its preview).
  const [wizardServers, setWizardServers] = useState<XaccelServer[]>([]);
  const [wizardServerIds, setWizardServerIds] = useState<number[]>([]);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardSports, setWizardSports] = useState<WizardSport[]>([]);
  const [expandedSports, setExpandedSports] = useState<Set<string>>(new Set());
  const [selectedSports, setSelectedSports] = useState<Set<string>>(new Set());
  const [selectedCompetitions, setSelectedCompetitions] = useState<Map<string, Set<string>>>(new Map());

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SportForm>({
    resolver: zodResolver(sportSchema),
    defaultValues: {
      name: '',
      dazn_asset_id: '',
    },
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sports.findIndex(s => s.id === active.id);
    const newIndex = sports.findIndex(s => s.id === over.id);
    const reordered = arrayMove(sports, oldIndex, newIndex);

    // Optimistic update
    setSports(reordered);

    // Persist to backend
    try {
      await sportsService.reorder(reordered.map(s => s.id));
    } catch (error) {
      toast.error(getErrorMessage(error) || t('sports.reorderError'));
      fetchSports(); // Rollback on error
    }
  };

  const fetchSports = async () => {
    try {
      const data = await sportsService.list();
      setSports(data);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSports();
  }, []);

  const openCreateModal = () => {
    setEditingSport(null);
    reset({
      name: '',
      dazn_asset_id: '',
    });
    setShowModal(true);
  };

  const openEditModal = (sport: Sport) => {
    setEditingSport(sport);
    reset({
      name: sport.name,
      dazn_asset_id: sport.dazn_asset_id || '',
    });
    setShowModal(true);
  };

  const onSubmit = async (data: SportForm) => {
    try {
      const payload = {
        name: data.name,
        dazn_asset_id: data.dazn_asset_id || undefined,
      };

      if (editingSport) {
        await sportsService.update(editingSport.id, payload);
        toast.success(t('common.updated'));
      } else {
        await sportsService.create(payload);
        toast.success(t('common.created'));
      }

      setShowModal(false);
      fetchSports();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await sportsService.delete(id);
      toast.success(t('common.deleted'));
      setDeleteConfirm(null);
      fetchSports();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const handleDeleteAll = async () => {
    try {
      await sportsService.deleteAll();
      toast.success(t('sports.allDeleted'));
      setDeleteAllConfirm(false);
      fetchSports();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  // Wizard functions
  const openWizard = async () => {
    setShowWizard(true);
    xaccelService.list().then((list) => {
      setWizardServers(list);
      const suggested = list.find((sv) => sv.is_default) || list[0];
      setWizardServerIds(suggested ? [suggested.id] : []);
    }).catch(() => { setWizardServers([]); setWizardServerIds([]); });
    setWizardLoading(true);
    setWizardSports([]);
    setExpandedSports(new Set());
    setSelectedSports(new Set());
    setSelectedCompetitions(new Map());

    try {
      const data = await sportsService.getWizardData();
      setWizardSports(data.sports);
      // Start with all sports collapsed - user expands manually
    } catch (error) {
      toast.error(getErrorMessage(error) || t('sports.wizardLoadError'));
      setShowWizard(false);
    } finally {
      setWizardLoading(false);
    }
  };

  const toggleSportExpanded = (sportAssetId: string) => {
    setExpandedSports(prev => {
      const next = new Set(prev);
      if (next.has(sportAssetId)) {
        next.delete(sportAssetId);
      } else {
        next.add(sportAssetId);
      }
      return next;
    });
  };

  const toggleSportSelected = (sport: WizardSport) => {
    setSelectedSports(prev => {
      const next = new Set(prev);
      if (next.has(sport.sport_asset_id)) {
        next.delete(sport.sport_asset_id);
        // Also deselect all competitions for this sport
        setSelectedCompetitions(compPrev => {
          const compNext = new Map(compPrev);
          compNext.delete(sport.sport_asset_id);
          return compNext;
        });
      } else {
        next.add(sport.sport_asset_id);
        // Auto-select all competitions (all are new - backend filters existing)
        const compIds = new Set<string>(sport.competitions.map((c: WizardCompetition) => c.competition_asset_id));
        setSelectedCompetitions(compPrev => {
          const compNext = new Map(compPrev);
          compNext.set(sport.sport_asset_id, compIds);
          return compNext;
        });
      }
      return next;
    });
  };

  const toggleCompetitionSelected = (sportAssetId: string, compAssetId: string) => {
    setSelectedCompetitions(prev => {
      const next = new Map(prev);
      const sportComps = next.get(sportAssetId) || new Set();
      const nextSportComps = new Set(sportComps);

      if (nextSportComps.has(compAssetId)) {
        nextSportComps.delete(compAssetId);
      } else {
        nextSportComps.add(compAssetId);
      }

      next.set(sportAssetId, nextSportComps);

      // Auto-select sport if any competition is selected
      if (nextSportComps.size > 0 && !selectedSports.has(sportAssetId)) {
        setSelectedSports(sPrev => new Set([...sPrev, sportAssetId]));
      }

      return next;
    });
  };

  const selectAll = () => {
    // Select all sports that have competitions available
    const sportsWithComps = wizardSports.filter(s => s.competitions.length > 0);
    const allSports = new Set(sportsWithComps.map(s => s.sport_asset_id));
    const allComps = new Map<string, Set<string>>();
    sportsWithComps.forEach(sport => {
      const compIds = new Set<string>(sport.competitions.map((c: WizardCompetition) => c.competition_asset_id));
      allComps.set(sport.sport_asset_id, compIds);
    });
    setSelectedSports(allSports);
    setSelectedCompetitions(allComps);
  };

  const deselectAll = () => {
    setSelectedSports(new Set());
    setSelectedCompetitions(new Map());
  };

  const expandAll = () => {
    // Only expand sports that have competitions
    setExpandedSports(new Set(wizardSports.filter(s => s.competitions.length > 0).map(s => s.sport_asset_id)));
  };

  const collapseAll = () => {
    setExpandedSports(new Set());
  };

  const getSelectedCount = useCallback(() => {
    let totalComps = 0;
    selectedCompetitions.forEach(comps => {
      totalComps += comps.size;
    });
    return { sports: selectedSports.size, competitions: totalComps };
  }, [selectedSports, selectedCompetitions]);

  const handleWizardImport = async () => {
    const counts = getSelectedCount();
    if (counts.sports === 0 && counts.competitions === 0) {
      toast.error(t('sports.selectAtLeastOne'));
      return;
    }

    setIsImporting(true);
    try {
      // Build selected sports array
      const sportsToImport = wizardSports
        .filter(s => selectedSports.has(s.sport_asset_id))
        .map(s => ({
          sport_asset_id: s.sport_asset_id,
          sport_name: s.sport_name,
        }));

      // Build selected competitions array
      const compsToImport: Array<{
        sport_asset_id: string;
        competition_asset_id: string;
        competition_name: string;
        image?: string | null;
      }> = [];

      wizardSports.forEach((sport: WizardSport) => {
        const sportComps = selectedCompetitions.get(sport.sport_asset_id);
        if (sportComps) {
          sport.competitions.forEach((comp: WizardCompetition) => {
            if (sportComps.has(comp.competition_asset_id)) {
              compsToImport.push({
                sport_asset_id: sport.sport_asset_id,
                competition_asset_id: comp.competition_asset_id,
                competition_name: comp.competition_name,
                image: comp.image,
              });
            }
          });
        }
      });

      const result = await sportsService.importFromWizard(sportsToImport, compsToImport, wizardServerIds);
      toast.success(result.message || t('sports.importedCount', { sports: counts.sports, competitions: counts.competitions }));
      setShowWizard(false);
      fetchSports();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('sports.importError'));
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('sports.title')}</h1>
          <p className="text-dark-400 mt-1">{sports.length} {t('sports.configuredCount')}</p>
        </div>
        <div className="flex items-center gap-2">
          {sports.length > 0 && (
            deleteAllConfirm ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={handleDeleteAll}
                  className="p-2 rounded-lg bg-error-600 text-white hover:bg-error-500"
                  title={t('common.confirmDelete')}
                >
                  <Check size={16} />
                </button>
                <button
                  onClick={() => setDeleteAllConfirm(false)}
                  className="p-2 rounded-lg hover:bg-dark-700 text-dark-400"
                  title={t('common.cancel')}
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setDeleteAllConfirm(true)}
                className="btn-secondary text-error-600 hover:bg-error-50"
              >
                <Trash2 size={16} />
                {t('common.deleteAll')}
              </button>
            )
          )}
          <button
            onClick={openWizard}
            disabled={wizardLoading}
            className="btn-secondary"
          >
            {wizardLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Download size={18} />
            )}
            {t('sports.wizard')}
          </button>
          <button onClick={openCreateModal} className="btn-primary">
            <Plus size={18} />
            {t('sports.add')}
          </button>
        </div>
      </div>

      {/* Grid with Drag-and-Drop */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-20 bg-dark-700 rounded" />
            </div>
          ))}
        </div>
      ) : sports.length === 0 ? (
        <div className="card">
          <p className="text-dark-400">{t('common.noData')}</p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sports.map(s => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sports.map((sport) => (
                <SortableSportCard
                  key={sport.id}
                  sport={sport}
                  deleteConfirm={deleteConfirm}
                  onEdit={openEditModal}
                  onDeleteConfirm={(id) => setDeleteConfirm(id)}
                  onDeleteCancel={() => setDeleteConfirm(null)}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div className="card w-full max-w-md m-4 animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-dark-100">
                {editingSport ? t('sports.editTitle') : t('sports.add')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="label">{t('sports.name')} *</label>
                <input
                  type="text"
                  className={errors.name ? 'input-error' : 'input'}
                  placeholder={t('sports.namePlaceholder')}
                  {...register('name')}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-error-500">
                    {t(errors.name.message || '')}
                  </p>
                )}
              </div>

              <div>
                <label className="label">{t('sports.assetId')}</label>
                <input
                  type="text"
                  className="input"
                  placeholder={t('sports.assetIdPlaceholder')}
                  {...register('dazn_asset_id')}
                  readOnly={!!editingSport?.dazn_asset_id}
                />
                <p className="text-xs text-dark-500 mt-1">
                  {t('sports.assetIdHelp')}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? t('common.loading') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Wizard Modal */}
      {showWizard && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowWizard(false)}
        >
          <div className="card w-full max-w-3xl m-4 max-h-[85vh] flex flex-col animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 shrink-0">
              <h2 className="text-xl font-semibold text-dark-100">
                {t('sports.wizardTitle')}
              </h2>
              <button
                onClick={() => setShowWizard(false)}
                className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400"
              >
                <X size={20} />
              </button>
            </div>

            {wizardLoading ? (
              <div className="flex-1 flex items-center justify-center py-12">
                <Loader2 size={32} className="animate-spin text-primary-500" />
                <span className="ml-3 text-dark-400">{t('sports.loadingFromDazn')}</span>
              </div>
            ) : wizardSports.filter(s => s.competitions.length > 0).length === 0 ? (
              <div className="flex-1 py-8 text-center text-dark-400">
                <p>{t('sports.noNewCompetitions')}</p>
                <p className="text-sm mt-2">{t('sports.allCompetitionsImported')}</p>
              </div>
            ) : (
              <>
                {/* Toolbar */}
                <div className="flex items-center gap-4 mb-4 pb-4 border-b border-dark-700 shrink-0 flex-wrap">
                  <div className="flex items-center gap-2">
                    <button onClick={expandAll} className="text-sm text-primary-600 hover:text-primary-700">
                      {t('common.expandAll')}
                    </button>
                    <span className="text-dark-600">|</span>
                    <button onClick={collapseAll} className="text-sm text-dark-400 hover:text-dark-300">
                      {t('common.collapseAll')}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={selectAll} className="text-sm text-success-600 hover:text-success-300">
                      {t('common.selectAll')}
                    </button>
                    <span className="text-dark-600">|</span>
                    <button onClick={deselectAll} className="text-sm text-dark-400 hover:text-dark-300">
                      {t('common.deselectAll')}
                    </button>
                  </div>
                  <span className="ml-auto text-sm text-dark-400">
                    {wizardSports.filter(s => s.competitions.length > 0).length} sport, {wizardSports.reduce((sum, s) => sum + s.competitions.length, 0)} {t('sports.competitions')}
                    {getSelectedCount().sports > 0 && (
                      <> — {t('sports.selected')}: {getSelectedCount().sports} sport, {getSelectedCount().competitions} {t('sports.competitions')}</>
                    )}
                  </span>
                </div>

                {/* Sports List - Only shows sports with new competitions, sorted alphabetically */}
                <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                  {wizardSports
                    .filter(sport => sport.competitions.length > 0)
                    .sort((a, b) => a.sport_name.localeCompare(b.sport_name))
                    .map(sport => (
                    <div key={sport.sport_asset_id} className="border border-dark-700 rounded-lg overflow-hidden">
                      {/* Sport Header */}
                      <div
                        className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-dark-800 ${
                          selectedSports.has(sport.sport_asset_id) ? 'bg-primary-50' : ''
                        }`}
                      >
                        <button
                          onClick={() => toggleSportExpanded(sport.sport_asset_id)}
                          className="p-1 hover:bg-dark-700 rounded"
                        >
                          {expandedSports.has(sport.sport_asset_id) ? (
                            <ChevronDown size={16} className="text-dark-400" />
                          ) : (
                            <ChevronRight size={16} className="text-dark-400" />
                          )}
                        </button>
                        <input
                          type="checkbox"
                          checked={selectedSports.has(sport.sport_asset_id)}
                          onChange={() => toggleSportSelected(sport)}
                          className="w-4 h-4 rounded border-dark-600 text-primary-500 focus:ring-primary-500 focus:ring-offset-dark-800"
                        />
                        <div className="flex-1 flex items-center gap-2" onClick={() => toggleSportExpanded(sport.sport_asset_id)}>
                          <span className="font-medium text-dark-100">{sport.sport_name}</span>
                          <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-dark-700 text-dark-400">
                            {sport.existing_competitions_count}/{sport.existing_competitions_count + sport.competitions.length}
                          </span>
                        </div>
                      </div>

                      {/* Competitions - All are new (backend filters existing ones), sorted alphabetically */}
                      {expandedSports.has(sport.sport_asset_id) && sport.competitions.length > 0 && (
                        <div className="bg-dark-800 border-t border-dark-700 px-4 py-2 space-y-1">
                          {[...sport.competitions].sort((a, b) => a.competition_name.localeCompare(b.competition_name)).map((comp: WizardCompetition) => (
                            <label
                              key={comp.competition_asset_id}
                              className={`flex items-center gap-3 p-2 rounded hover:bg-dark-800 cursor-pointer ${
                                selectedCompetitions.get(sport.sport_asset_id)?.has(comp.competition_asset_id)
                                  ? 'bg-primary-50'
                                  : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedCompetitions.get(sport.sport_asset_id)?.has(comp.competition_asset_id) || false}
                                onChange={() => toggleCompetitionSelected(sport.sport_asset_id, comp.competition_asset_id)}
                                className="w-4 h-4 rounded border-dark-600 text-primary-500 focus:ring-primary-500 focus:ring-offset-dark-800"
                              />
                              <span className="text-dark-200">{comp.competition_name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {wizardServers.length > 0 && (
                  <div className="pt-3 mt-3 border-t border-dark-700 shrink-0">
                    <p className="text-sm text-dark-300">{t('sports.wizardServers')}</p>
                    <div className="flex flex-wrap gap-3 mt-1">
                      {wizardServers.map((sv) => (
                        <label key={sv.id} className="flex items-center gap-1.5 text-sm text-dark-200 cursor-pointer">
                          <input type="checkbox" checked={wizardServerIds.includes(sv.id)} disabled={isImporting}
                            onChange={() => setWizardServerIds((prev) => (prev.includes(sv.id) ? prev.filter((x) => x !== sv.id) : [...prev, sv.id]))} />
                          {sv.name}
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-dark-500 mt-1">{t('sports.wizardServersHint')}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-dark-700 shrink-0">
                  <button
                    onClick={() => setShowWizard(false)}
                    className="btn-secondary"
                    disabled={isImporting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleWizardImport}
                    disabled={isImporting || (getSelectedCount().sports === 0 && getSelectedCount().competitions === 0)}
                    className="btn-primary"
                  >
                    {isImporting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {t('common.importing')}
                      </>
                    ) : (
                      <>
                        <Download size={18} />
                        {t('sports.importSelected')}
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
